import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { Box, Typography } from '@monorepo/nova-ui';
import { PASSWORD_DISPLAY } from '../constants';
import {
  getAnimatedPasswordStyles,
  getPasswordContainerStyles,
} from '../styles';
import { AnimatedPasswordTextProps } from '../types';

export const AnimatedPasswordText: FunctionComponent<
  AnimatedPasswordTextProps
> = ({ value, isPassword, showPassword }) => {
  const { t } = useTranslation(['login']);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldShowPassword, setShouldShowPassword] = useState(showPassword);

  useEffect(() => {
    if (isPassword) {
      if (showPassword) {
        // When showing password, animate the transition with delay
        setIsAnimating(true);
        const timeout = setTimeout(() => {
          setShouldShowPassword(true);
          setIsAnimating(false);
        }, 150);
        return () => clearTimeout(timeout);
      } else {
        // When hiding password, immediately switch (no delay)
        setShouldShowPassword(false);
        setIsAnimating(false);
      }
    } else {
      setShouldShowPassword(showPassword);
    }
  }, [showPassword, isPassword]);

  // When hiding (showPassword is false), immediately use dots (no delay)
  // When showing (showPassword is true), use delayed state for smooth animation
  const effectiveShowPassword = isPassword
    ? showPassword
      ? shouldShowPassword
      : false
    : showPassword;

  // Calculate displayValue directly to avoid glitch
  const displayValue =
    isPassword && !effectiveShowPassword ? PASSWORD_DISPLAY.HIDDEN_TEXT : value;

  const handleLongPassword = (text: string): string => {
    // Don't truncate password with ellipsis - let it wrap or overflow naturally
    return text;
  };

  const processedValue = handleLongPassword(displayValue);

  // Determine if we're showing the actual password or dots based on displayValue
  const isShowingActualPassword = displayValue !== PASSWORD_DISPLAY.HIDDEN_TEXT;

  // Show tooltip for long passwords or when password is hidden
  const shouldShowTooltip =
    isPassword &&
    ((!showPassword && value.length > 8) ||
      (showPassword && value.length > PASSWORD_DISPLAY.TRUNCATE_LENGTH) ||
      (!showPassword && value.length > 0));

  const content = (
    <Box sx={getPasswordContainerStyles}>
      <Typography
        variant="body2"
        fontWeight={400}
        className={isAnimating ? 'password-changing' : ''}
        sx={(theme) =>
          getAnimatedPasswordStyles(theme, isPassword, isShowingActualPassword)
        }
      >
        {processedValue}
      </Typography>
    </Box>
  );

  if (shouldShowTooltip) {
    return (
      <Tooltip
        title={
          showPassword
            ? t('login:traders_login.full_password', {
                defaultValue: 'Full password: {{password}}',
                password: value,
              })
            : t('login:traders_login.password_tooltip', {
                defaultValue: 'Password: {{password}}',
                password: value,
              })
        }
        placement="top"
        arrow
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};
